// heavily inspired by: https://github.com/getsentry/sentry/issues/22715#issuecomment-926385843
import { type EventHint, type Scope, type Span } from "@sentry/react";
import type { SetRequired } from "type-fest";
import { ignoreErrors, ignoreWithBeforeSend } from "./ignore";
import { assetLoadErrorBeforeSend, installAssetLoadTelemetry } from "./assetLoadTelemetry";
import {
  composeBeforeBreadcrumb,
  composeBeforeSend,
  teamAttributionBeforeSend,
  enrichedErrorBeforeSend,
  featureSwitchBeforeSend,
  redactBreadcrumbs,
  addHeadersToBreadcrumbs,
} from "./utils";
import { type ErrorInfo } from "react";

type SentryTypes = typeof import("@sentry/react");

export const isDev = typeof window !== "undefined" && window.location.hostname.endsWith(".test");

// A page load only gets a release health session once this chunk has loaded, because the session is
// started from inside `Sentry.init`. Asset requests fail in bursts right after a deploy — which is
// exactly the canary window the crash-free-rate gate measures — and a page load that loses this
// chunk contributes no session at all while other page loads still report the same wave of errors.
// A couple of cheap retries keep those sessions in the denominator (WEBP-523).
const SENTRY_IMPORT_ATTEMPTS = 3;
const SENTRY_IMPORT_RETRY_DELAY_MS = 500;

export const retryAsync = async <T>(
  attempt: () => Promise<T>,
  { attempts, delayMs }: { attempts: number; delayMs: number },
): Promise<T> => {
  let lastError: unknown;

  for (let tries = 0; tries < attempts; tries++) {
    try {
      return await attempt();
      // eslint-disable-next-line no-catch-all/no-catch-all -- a retry helper has to treat every failure the same; the last one is rethrown below.
    } catch (error) {
      lastError = error;
      if (tries < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * (tries + 1)));
      }
    }
  }

  throw lastError;
};

const queue = [] as ((sentry: SentryTypes) => void)[];
const errorQueue = [] as Parameters<OnErrorEventHandlerNonNull>[];
const rejectionQueue = [] as PromiseRejectionEvent[];

// These functions will push calls into a queue that will be flushed once Sentry has loaded,
// they will then be replaced by direct calls to Sentry
export let addBreadcrumb: SentryTypes["addBreadcrumb"] = (...args) => {
  queue.push((x) => x.addBreadcrumb(...args));
};
export let captureMessage: SentryTypes["captureMessage"] = (...args) => {
  queue.push((x) => x.captureMessage(...args));
  return "";
};
export let captureException: SentryTypes["captureException"] = (...args) => {
  queue.push((x) => x.captureException(...args));
  return "";
};
export let captureEvent: SentryTypes["captureEvent"] = (...args) => {
  queue.push((x) => x.captureEvent(...args));
  return "";
};
export let startSpan = (
  options: Parameters<SentryTypes["startSpan"]>[0],
  callback: (span: Span | undefined) => unknown,
) => {
  queue.push((x) => x.startSpan(options, callback));
};

type BrowserTracingPageLoadSpanOptions = Parameters<SentryTypes["startBrowserTracingPageLoadSpan"]>[1];
type BrowserTracingNavigationSpanOptions = Parameters<SentryTypes["startBrowserTracingNavigationSpan"]>[1];
type BrowserTracingNavigationOptions = Parameters<SentryTypes["startBrowserTracingNavigationSpan"]>[2];

export let startBrowserTracingPageLoadSpan = (spanOptions: BrowserTracingPageLoadSpanOptions) => {
  queue.push((x) => {
    const client = x.getClient();
    if (client) x.startBrowserTracingPageLoadSpan(client, spanOptions);
  });
};
export let startBrowserTracingNavigationSpan = (
  spanOptions: BrowserTracingNavigationSpanOptions,
  options?: BrowserTracingNavigationOptions,
) => {
  queue.push((x) => {
    const client = x.getClient();
    if (client) x.startBrowserTracingNavigationSpan(client, spanOptions, options);
  });
};
export let withScope = (callback: (scope: Scope) => void): void => {
  queue.push((x) => x.withScope(callback));
};
export let setUser: SentryTypes["setUser"] = (...args) => {
  queue.push((x) => x.setUser(...args));
  return "";
};
export let setContext: SentryTypes["setContext"] = (...args) => {
  queue.push((x) => x.setContext(...args));
  return "";
};
export let setExtra: SentryTypes["setExtra"] = (...args) => {
  queue.push((x) => x.setExtra(...args));
  return "";
};
export let setExtras: SentryTypes["setExtras"] = (...args) => {
  queue.push((x) => x.setExtras(...args));
  return "";
};

export let setTag: SentryTypes["setTag"] = (...args) => {
  queue.push((x) => x.setTag(...args));
  return "";
};

type BaseInitOptions = Parameters<SentryTypes["init"]>[0];
type ReactRouterBrowserTracingParams = Parameters<SentryTypes["reactRouterV6BrowserTracingIntegration"]>[0];

type InitOptions = SetRequired<
  Omit<BaseInitOptions, "integrations" | "replaysSessionSampleRate" | "replaysOnErrorSampleRate">,
  "dsn"
> & {
  replays?: {
    sessionSampleRate?: number;
    onErrorSampleRate?: number;
    useCompression?: boolean;
    mask?: (string: string) => string;
  };
  tracing?: {
    reactRouter?: {
      useEffect: ReactRouterBrowserTracingParams["useEffect"];
      useLocation: ReactRouterBrowserTracingParams["useLocation"];
      useNavigationType: ReactRouterBrowserTracingParams["useNavigationType"];
      createRoutesFromChildren: ReactRouterBrowserTracingParams["createRoutesFromChildren"];
      matchRoutes: ReactRouterBrowserTracingParams["matchRoutes"];
    };
  };
};

// A release-health session is what the canary deploy gate counts (crash_free_rate(session) for
// `release:<appVersion>`, see packages/delivery/src/scripts/monitor-canary-crash-target.js). The
// SDK drops EVERY session envelope when it cannot resolve a release string, but keeps sending
// error events — so a build that ships an unusable release reports crashes with no sessions to
// divide them by, which is exactly how a canary ends up with an impossible crash rate.
// `%APP_VERSION%` is the literal placeholder left behind when Vite's index.html env substitution
// does not run (see the dojo-error-sos tag's `data-app-version` in each app's index.html), and
// "undefined"/"null" are what a missing build-time value stringifies to.
const UNUSABLE_RELEASES = new Set(["", "undefined", "null", "%APP_VERSION%"]);

export const isUsableRelease = (release: unknown): release is string =>
  typeof release === "string" && !UNUSABLE_RELEASES.has(release.trim());

/**
 * First usable release among `candidates`, or `undefined` when none is.
 *
 * Apps have two independent ways to reach the same build-time APP_VERSION — the `%APP_VERSION%`
 * substitution in index.html (`window.appVersion`) and the `Config.*` build define — so passing
 * both means one of them failing no longer costs the release, and with it every session for the
 * release the canary gate is watching.
 */
// `find` widens to `unknown`; the `isUsableRelease` type guard already narrowed each candidate to
// a non-placeholder string, so the cast only restates what the predicate proved.
export const resolveRelease = (...candidates: unknown[]): string | undefined =>
  candidates.find((candidate) => isUsableRelease(candidate)) as string | undefined;

const resolveReleaseForInit = (release: unknown): string | undefined => {
  const resolved = resolveRelease(release);

  if (!resolved && !isDev) {
    // eslint-disable-next-line no-console -- Sentry is the broken thing here, so it cannot report its own missing release; the console is the only channel left.
    console.error(
      `[telemetry] Sentry release is unusable (${String(release)}); release health sessions will not be recorded.`,
    );
  }

  return resolved;
};

// Suppress Sentry's auto-emitted URL-named pageload/navigation spans; useRouteSpans emits route-
// named ones via startBrowserTracingPageLoadSpan / NavigationSpan, which still trigger the
// integration's web-vitals listener so LCP/CLS/FCP/TTFB attach. The remaining flags are pinned
// explicitly to defend against silent default flips on @sentry/react major upgrades.
const sharedBrowserTracingOptions = {
  instrumentPageLoad: false,
  instrumentNavigation: false,
  enableInp: true,
  enableLongAnimationFrame: true,
  _experiments: { enableInteractions: false, enableStandaloneClsSpans: true },
} as const;

const getRoutingInstrumentation = (sentry: SentryTypes, options: InitOptions["tracing"]) => {
  if (options?.reactRouter) {
    const { useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } = options.reactRouter;
    return sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      ...sharedBrowserTracingOptions,
    });
  }

  return undefined;
};

export const init = async (options: InitOptions) => {
  if (typeof window === "undefined") return;

  // Before the Sentry chunk is awaited on purpose: the failure this instruments is an asset that
  // does not load, and the Sentry chunk is itself such an asset. Breadcrumbs recorded now queue
  // and are replayed once the SDK is in place.
  installAssetLoadTelemetry({ addBreadcrumb });

  const oldOnError = window.onerror;
  const oldOnUnhandledRejection = window.onunhandledrejection;
  window.onerror = (...args) => errorQueue.push(args);
  window.onunhandledrejection = (e: PromiseRejectionEvent) => rejectionQueue.push(e);

  const Sentry = await retryAsync(() => import("@sentry/react"), {
    attempts: SENTRY_IMPORT_ATTEMPTS,
    delayMs: SENTRY_IMPORT_RETRY_DELAY_MS,
  });
  window.onerror = oldOnError;
  window.onunhandledrejection = oldOnUnhandledRejection;

  // Never hand Sentry a placeholder release: it would tag events with a release nothing queries
  // while silently discarding every session (Client.sendSession bails on an empty release), which
  // undercounts healthy sessions without undercounting the errors measured against them.
  const release = resolveReleaseForInit(options.release);

  Sentry.init({
    ...options,
    release,
    debug: options.debug ?? isDev,
    environment: options.environment ?? (isDev ? "development" : "production"),
    ignoreTransactions: [],
    beforeSend: composeBeforeSend(
      ignoreWithBeforeSend,
      teamAttributionBeforeSend,
      assetLoadErrorBeforeSend,
      enrichedErrorBeforeSend,
      // Last of the ClassDojo steps: fills in ambient switch state without overwriting
      // anything an earlier step or the capture site set. See WEBP-544.
      featureSwitchBeforeSend,

      // This file is typed by several different tsconfigs which conflict with each other
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore Type 'Event' is not assignable to type 'ErrorEvent'.
      options.beforeSend,
    ),
    beforeBreadcrumb: composeBeforeBreadcrumb(redactBreadcrumbs, addHeadersToBreadcrumbs),
    ignoreErrors: [...ignoreErrors, ...(options.ignoreErrors ?? [])],
    integrations: [
      // Pinned rather than left to the SDK defaults: this is the integration that starts a
      // release-health session on every page load (and on every client-side route change), which
      // is the denominator the canary crash-free-rate gate divides crashes by. It must cover the
      // whole population the gate judges, not just the sessions that happen to error.
      Sentry.browserSessionIntegration(),
      options.tracing?.reactRouter
        ? getRoutingInstrumentation(Sentry, options.tracing)
        : Sentry.browserTracingIntegration(sharedBrowserTracingOptions),
      options.replays &&
        Sentry.replayIntegration({
          maskFn: (s: string) => {
            return options.replays?.mask ? options.replays.mask(s) : "*".repeat(s.length);
          },
          ...(options.replays?.useCompression === undefined ? {} : { useCompression: options.replays.useCompression }),
        }),
    ].filter((val) => !!val),

    tracesSampleRate: options.tracesSampleRate ?? 0.01,
    replaysSessionSampleRate: options.replays?.sessionSampleRate,
    replaysOnErrorSampleRate: options.replays?.onErrorSampleRate,
  });

  // getReplay() is still available in v10 and returns the Replay integration instance
  (window as Window & { replay?: unknown }).replay = Sentry.getReplay();

  // Override the placeholder functions with the real ones
  addBreadcrumb = Sentry.addBreadcrumb.bind(Sentry);
  captureMessage = Sentry.captureMessage.bind(Sentry);
  captureException = Sentry.captureException.bind(Sentry);
  captureEvent = Sentry.captureEvent.bind(Sentry);
  startSpan = Sentry.startSpan.bind(Sentry);
  startBrowserTracingPageLoadSpan = (spanOptions) => {
    const client = Sentry.getClient();
    if (client) Sentry.startBrowserTracingPageLoadSpan(client, spanOptions);
  };
  startBrowserTracingNavigationSpan = (spanOptions, navOptions) => {
    const client = Sentry.getClient();
    if (client) Sentry.startBrowserTracingNavigationSpan(client, spanOptions, navOptions);
  };
  withScope = Sentry.withScope.bind(Sentry);
  setUser = Sentry.setUser.bind(Sentry);
  setContext = Sentry.setContext.bind(Sentry);
  setExtra = Sentry.setExtra.bind(Sentry);
  setExtras = Sentry.setExtras.bind(Sentry);
  setTag = Sentry.setTag.bind(Sentry);
  // startTransaction = Sentry.startTransaction.bind(Sentry);

  // TODO: React ErrorBoundary

  // Replay queued calls and errors through Sentry's handlers
  queue.forEach((call) => call(Sentry));
  errorQueue.forEach((x) => window.onerror?.(...x));
  rejectionQueue.forEach((e) => window.onunhandledrejection?.(e));
};

/*! Extracted from sentry/react: https://github.com/getsentry/sentry-javascript/blob/8f4d56fa1efc82a62d474ad88d96e4da36279d2a/packages/react/src/error.ts */
/**
 * Recurse through `error.cause` chain to set cause on an error.
 */

function setCause(error: Error & { cause?: Error }, cause: Error): void {
  const seenErrors = new WeakSet();

  function recurse(error: Error & { cause?: Error }, cause: Error): void {
    // If we've already seen the error, there is a recursive loop somewhere in the error's
    // cause chain. Let's just bail out then to prevent a stack overflow.
    if (seenErrors.has(error)) {
      return;
    }
    if (error.cause) {
      seenErrors.add(error);
      return recurse(error.cause as Error & { cause?: Error }, cause);
    }

    try {
      error.cause = cause;
    } catch (x) {
      if (!(x instanceof Error)) {
        throw x;
      }

      captureException(x, { extra: { error, cause } });
    }
  }

  recurse(error, cause);
}

export type CaptureReactExceptionOptions = {
  /**
   * Pass `true` ONLY from an app-root error boundary whose fallback replaces the entire app.
   * It reports the event at `level: "fatal"`, which is what makes `level:fatal` mean "this blew up
   * the app" for web — the severity tier Sentry sorts, digests, and filters the issue stream by.
   * It also forces `mechanism.handled: false`, which marks the Sentry session "crashed" and so
   * feeds the Release Health crash rate. This is the ONLY thing in web that crashes a session:
   * a recoverable surface (banner, offline fallback, subtree boundary) must not, or the crash
   * rate measures dismissible banners instead of dead apps. See WEBP-516.
   *
   * This is deliberately narrower than the `app_broken` tag: `app_broken` marks any terminal
   * user-facing surface (banner, network fallback, dead-end page), while `fatal` marks only a dead
   * app. Subtree boundaries that render a local fallback and let the rest of the app keep working
   * must leave this unset so they report at the default level. Fatality belongs to the call site,
   * never to this helper.
   */
  appIsDead?: boolean;
};

export function captureReactException(
  error: unknown,
  { componentStack }: ErrorInfo,
  // Narrower than `captureException`'s parameter, which is an exclusive union of EventHint
  // and CaptureContext: every caller passes an `appBroken.ts` hint builder's EventHint, and
  // only that branch lets the `appIsDead` override below reach `mechanism`.
  hint?: EventHint,
  options: CaptureReactExceptionOptions = {},
) {
  // If on React version >= 17, create stack trace from componentStack param and links
  // to to the original error using `error.cause` otherwise relies on error param for stacktrace.
  // Linking errors requires the `LinkedErrors` integration be enabled.
  // See: https://reactjs.org/blog/2020/08/10/react-v17-rc.html#native-component-stacks
  //
  // Although `componentDidCatch` is typed to accept an `Error` object, it can also be invoked
  // with non-error objects. This is why we need to check if the error is an error-like object.
  // See: https://github.com/getsentry/sentry-javascript/issues/6167
  if (componentStack) {
    const isError = error instanceof Error;
    const errorBoundaryError = new Error(isError ? error.message : String(error));
    errorBoundaryError.name = `React ErrorBoundary ${isError ? error.name : "Error"}`;
    errorBoundaryError.stack = componentStack;

    // Using the `LinkedErrors` integration to link the errors together.
    if (isError) {
      setCause(error as Error & { cause?: Error }, errorBoundaryError);
    }
  }

  withScope((scope) => {
    scope.setContext("react", { componentStack });

    // Set on the scope, not in `hint`: callers pass `unhandledHint()`, whose nested
    // `captureContext` carries no `level`, so Sentry's clone-and-merge leaves this intact.
    if (options.appIsDead) {
      scope.setLevel("fatal");
    }

    // The mechanism, unlike the level, has to ride on the hint — it is not scope state.
    // Overriding it here rather than at the call sites keeps "the app is dead" a single
    // decision: the same hint object is shared with the recoverable `captureException`
    // branch in every app's error handler, and only this path may crash the session.
    return captureException(
      error,
      options.appIsDead ? { ...hint, mechanism: { type: "generic", handled: false } } : hint,
    );
  });
}
